import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import ViewSDKClient from './ViewSDKClient';
import { getFileUrlById } from '../../models/api/files';
import { isPublicPage, PdfMatchParams } from '../../scripts/utils';
import {
  getDownloadLinkForTrashItem,
  getFilesystemFileLink,
  getPublicFilesystemFileLink,
} from '../../models/api/filesystem';
import { TextField } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import { AdobeViewerAPIs, AdobeViewerSearchController } from './interfaces';

function useScript(src: string) {
  // Keep track of script status ("idle", "loading", "ready", "error")
  const [status, setStatus] = useState(src ? 'loading' : 'idle');
  useEffect(() => {
    // Allow falsy src value if waiting on other data needed for
    // constructing the script URL passed to this hook.
    if (!src) {
      setStatus('idle');
      return;
    }
    // Fetch existing script element by src
    // It may have been added by another intance of this hook
    let script: any = document.querySelector(`script[src="${src}"]`);
    if (!script) {
      // Create script
      script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-status', 'loading');
      // Add script to document body
      document.body.appendChild(script);
      // Store status in attribute on script
      // This can be read by other instances of this hook
      const setAttributeFromEvent = (event: any) => {
        script.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error');
      };
      script.addEventListener('load', setAttributeFromEvent);
      script.addEventListener('error', setAttributeFromEvent);
    } else {
      // Grab existing script status from attribute and set to state.
      setStatus(script.getAttribute('data-status'));
    }
    // Script event handler to update status in state
    // Note: Even if the script already exists we still need to add
    // event handlers to update the state for *this* hook instance.
    const setStateFromEvent = (event: any) => {
      setStatus(event.type === 'load' ? 'ready' : 'error');
    };
    // Add event listeners
    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);
    // Remove event listeners on cleanup
    // eslint-disable-next-line consistent-return
    return () => {
      if (script) {
        script.removeEventListener('load', setStateFromEvent);
        script.removeEventListener('error', setStateFromEvent);
      }
    };
  }, [src]);

  return status;
}

export default function PDFViewer() {
  const params = useParams<PdfMatchParams>();
  const searchParams = new URLSearchParams(window.location.search);

  const initialSearchQuery = searchParams.get('query');

  const [initialized, setInitialized] = useState(false);

  const [searchQueryInput, setSearchQueryInput] = useState<string>(initialSearchQuery || '');
  const [searchQuery, setSearchQuery] = useState<string | null>(initialSearchQuery);

  const viewerApisRef = useRef<AdobeViewerAPIs>();
  const searchApiRef = useRef<AdobeViewerSearchController>();

  const [totalResults, setTotalResults] = useState(0);
  const [currentResult, setCurrentResult] = useState(0);

  const isBufferMode = searchParams.get('buffer') === 'true';

  const fetchUrl = () =>
    getFileUrlById(params.fileId)
      .then((result) => {
        return { fileName: result.file?.name, link: result.downloadLink! };
      })
      .catch(() => {
        return {
          fileName: 'Bodea Brochure',
          link: 'https://acrobatservices.adobe.com/view-sdk-demo/PDFs/Bodea Brochure.pdf',
        };
      });

  const fetchFilesystemUrl = () => {
    if (isPublicPage()) {
      return getPublicFilesystemFileLink(
        localStorage.getItem('projectId')!,
        localStorage.getItem('fullKey')!,
        localStorage.getItem('publicAccessKey')!,
        localStorage.getItem('designFileShareId')!,
      );
    }

    const s3Key = localStorage.getItem('s3Key');

    if (s3Key) {
      return getDownloadLinkForTrashItem(s3Key);
    }

    return getFilesystemFileLink(
      localStorage.getItem('projectId')!,
      localStorage.getItem('fullKey')!,
      true,
      localStorage.getItem('ownerId')!,
    );
  };

  const searchInit = async (viewerApis?: AdobeViewerAPIs, newSearchQuery?: string) => {
    const query = newSearchQuery || searchQuery;
    if (!query) return;

    try {
      const newSearchApi = await (viewerApis || viewerApisRef.current)?.search(query);
      if (!newSearchApi) return;

      await newSearchApi.onResultsUpdate(({ currentResult, totalResults }) => {
        setTotalResults(totalResults || 0);
        setCurrentResult(currentResult?.index ?? 0);
      });

      searchApiRef.current = newSearchApi;
    } catch (err) {
      setTotalResults(0);
      setCurrentResult(0);
    }
  };

  const showAttachmentPdf = async (buffer: ArrayBuffer, fileName: string) => {
    const viewSDKClient = new ViewSDKClient();
    await viewSDKClient.ready();

    const viewer = await viewSDKClient.previewFile(
      'adobe-dc-view',
      {
        showAnnotationTools: false,
        showLeftHandPanel: true,
        showPageControls: true,
        showDownloadPDF: true,
        showPrintPDF: true,
        enableSearchAPIs: !!searchQuery,
      },
      buffer,
      fileName,
    );

    if (searchQuery) {
      const apis = await viewer.getAPIs();
      await searchInit(apis);

      viewerApisRef.current = apis;
    }

    setInitialized(true);
  };

  const showPdf = async (url: string, fileName: string) => {
    const viewSDKClient = new ViewSDKClient();
    await viewSDKClient.ready();
    const viewer = await viewSDKClient.previewFile(
      'adobe-dc-view',
      {
        showAnnotationTools: false,
        showLeftHandPanel: true,
        showPageControls: true,
        showDownloadPDF: true,
        showPrintPDF: true,
        enableSearchAPIs: !!searchQuery,
      },
      url,
      fileName,
    );

    if (searchQuery) {
      const apis = await viewer.getAPIs();
      await searchInit(apis);

      viewerApisRef.current = apis;
    }

    setInitialized(true);
  };

  useScript('https://acrobatservices.adobe.com/view-sdk/viewer.js');

  const clearLocalStorage = () => {
    localStorage.removeItem('projectId');
    localStorage.removeItem('fullKey');
    localStorage.removeItem('fullName');
    localStorage.removeItem('ownerId');
    localStorage.removeItem('designFileShareId');
    localStorage.removeItem('publicAccessKey');
    localStorage.removeItem('s3Key');
  };

  useEffect(() => {
    if (isBufferMode) {
      const fileName = searchParams.get('fileName') || 'attachment.pdf';
      const channel = new BroadcastChannel('pdf_attachment');

      channel.postMessage('ready');

      channel.onmessage = (event) => {
        const { buffer, fileName: receivedFileName } = event.data;
        showAttachmentPdf(buffer, receivedFileName || fileName);
        channel.close();
      };

      return () => channel.close();
    }

    if (params.fileId === 'design') {
      fetchFilesystemUrl().then((link) => showPdf(link!, localStorage.getItem('fullName')!));
    } else {
      fetchUrl().then(({ fileName, link }) => showPdf(link, fileName || 'File Name Not Found'));
    }

    window.addEventListener('beforeunload', clearLocalStorage);

    return () => {
      window.removeEventListener('beforeunload', clearLocalStorage);
    };
  }, []);

  return (
    <>
      <div id="adobe-dc-view" style={{ height: '100vh' }} />
      {searchQuery && initialized ? (
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            top: 64,
            right: 80,
            padding: '12px',
            boxShadow: '0 3px 6px rgba(0,0,0,.05)',
            backgroundColor: '#FFF',
            borderRadius: 4,
          }}
        >
          <TextField
            size="small"
            variant="outlined"
            value={searchQueryInput}
            onChange={(e) => setSearchQueryInput(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setSearchQuery(searchQueryInput);
                await searchInit(undefined, searchQueryInput);
              }
            }}
            InputProps={{ style: { height: 32 } }}
          />
          <Typography>
            {currentResult} of {totalResults}
          </Typography>
          <IconButton
            onClick={async () => {
              const success = await searchApiRef.current?.previous();
              if (!success) {
                await searchInit();
              }
            }}
            style={{ padding: 0 }}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={async () => {
              const success = await searchApiRef.current?.next();
              if (!success) {
                await searchInit();
              }
            }}
            style={{ padding: 0 }}
          >
            <ChevronRight />
          </IconButton>
        </div>
      ) : null}
    </>
  );
}
